import React, { useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import classNames from 'classnames';
import { MappingConnectors } from './MappingConnectors/MappingConnectors';
import { MappingContext } from '@decisions/server/DecisionsFramework/Design/Flow/Service';
import { contextProviderNodeReducer } from 'components/property-editors/contextProviderNodeReducer';
import { debounce } from 'debounce';
import { compareEditorChildren, getChildrenFromSnapshot } from 'components/property-editors/utils/ProviderNodeUtils';
import { IAddBeforeSavePromises } from 'hooks/useBeforeSavePromises';
import { useSubscribeToEditingContext } from 'components/property-editors/hooks/useSubscribeToEditingContext';
import { useUpdatesPropertyEditors } from 'components/property-editors/hooks/useUpdatesPropertyEditors';
import {
  defaultPropertyEditorContext,
  PropertyEditorContext,
  propertyEditorContextHelper,
  PropertyEditorContextProvider,
} from 'components/property-editors/contexts/PropertyEditorContext';
import { IAwaitableContext } from 'contexts/AwaitableContext';
import { FlowMappingColumn } from './FlowMappingColumn';
import { StepMappingColumn } from './StepMappingColumn';
import { getMappings } from './MappingConnectors/MappingConnectorUtils';
import {
  createConditionPerFilter as createStepMappingFilter,
  getStepNodeData,
  stepNodeSearch,
} from './utils/MappingUtils';
import {
  filteredFlowNodesSelector,
  getBothFiltersUnmapped,
  getEditingContextId,
  getExpandedStepNodes,
  getStepFilter,
  getStepSearch,
  MappingEditorStore,
  MappingPanelType,
  storeIdSelector,
  useCreateMappingEditorStore,
  visibleFlowNodesSelector,
} from './MappingEditorStore';

export interface IMappingEditorProps extends IAddBeforeSavePromises {
  flowSessionId: string;
  flowStepId: string;
  forInput?: boolean;
  outputMappingContextId?: string;
  forCustom?: boolean;

  /** some designers have a sessionId, and some of their editors will depend on it */
  sessionId?: string;
  mappingContext?: MappingContext;
  /**
   * A callback that needs to be _invoked_ and `await`ed before proceeding.
   */
  //  addBeforeSaveCallback?: (path: string[], callback: BeforeSaveCallback) => void;
  /**
   * Set the tab index for a particular property grid.
   * Editors will share the tabindex, and will be navigated by DOM order.
   */
  tabIndex?: number;
  /**
   * Kind of a hack, but tell parent what the editing context id is, so it's managed here, but
   * can be referenced outside.
   */
  setEditingContextId?: (id: string) => void;
}

/**
 * Mapping editor for mapping inputs/outputs to flow steps
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function MappingEditor(props: IMappingEditorProps) {
  const {
    flowSessionId,
    forInput = true,
    flowStepId,
    addBeforeSavePromise,
    removeBeforeSavePromise,
    sessionId,
    setEditingContextId,
    forCustom,
    tabIndex = 1,
  } = props;

  const useStore = useCreateMappingEditorStore(flowSessionId, flowStepId, forInput, forCustom);
  const id = useStore(storeIdSelector);
  const stepSearch = useStore(getStepSearch);
  const stepFilter = useStore(getStepFilter);
  const bothFiltersUnmapped = useStore(getBothFiltersUnmapped);
  const setIsNodesLoading = useStore(store => store.setIsNodesLoading);
  const expandedStepNodes = useStore(getExpandedStepNodes);
  const dropConnector = useStore(state => state.dropConnector);
  const cacheMappings = useStore(state => state.setDerivedMappings);
  const setMappedFlowDataPaths = useStore(state => state.setMappedFlowDataPaths);
  const setSearchExpandedNodes = useStore(state => state.setSearchExpandedNodes);
  const visibleFlowDataNodes = useStore(visibleFlowNodesSelector);
  const filteredFlowDataNodes = useStore(filteredFlowNodesSelector);

  let { mappingContext } = useContext(PropertyEditorContext);
  if (props.mappingContext) mappingContext = props.mappingContext;

  const prevEditingContext = useRef<string>();
  // be very careful copying to props to state. This is generally an anti-pattern except for edge cases like this:
  const [snapshot, dispatchNodeChange] = useReducer(contextProviderNodeReducer, undefined);
  //17ms is roughly 60fps. Make this an app-wide setting?
  const debouncedUpdate = useMemo(() => debounce(dispatchNodeChange, 17), [dispatchNodeChange]);
  const snapshotRef = useRef(snapshot);
  const editingContext = useStore(getEditingContextId);
  // but also using refs for these since we don't want to update the useMemo any time these change.
  snapshotRef.current = snapshot;

  const { refresh, onChange: onEditorChange, addEditPromise, removeEditPromise } = useUpdatesPropertyEditors({
    editingContext,
    dispatchNodeChange,
    debouncedUpdate,
    addBeforeSavePromise,
    removeBeforeSavePromise,
    setIsNodesLoading,
  });

  const allEditors = useMemo(() => {
    return snapshot?.Children.sort(compareEditorChildren) || [];
  }, [snapshot?.Children]);

  /**
   * Filter context based on local search string state
   */
  const filteredEditors = useMemo(() => {
    let search = stepSearch.trim();
    const children = getChildrenFromSnapshot(snapshot);
    const mappingFilter = createStepMappingFilter(stepFilter);
    const setExpanded = (nodes: Map<string, boolean>) => setSearchExpandedNodes(nodes, MappingPanelType.STEP);
    // filter all, and sort top level of nodes:
    return stepNodeSearch(search, children, setExpanded, [mappingFilter]).sort(compareEditorChildren);
  }, [setSearchExpandedNodes, snapshot, stepFilter, stepSearch]);

  useEffect(() => {
    // handle fetching snapshot when only ID is passed
    if (editingContext !== prevEditingContext.current) {
      // whenever there's a new editing context id:
      setEditingContextId?.(editingContext);
      if (!snapshot) {
        refresh();
        return;
      }
    }

    // fire this effect only for editingContext and snapshot changes:
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingContext, snapshot]);

  useSubscribeToEditingContext({
    debouncedUpdate,
    editingContext,
    prevEditingContext,
    snapshot,
    refresh,
    skipValidationResetOnEachUpdate: true,
  });

  const context = propertyEditorContextHelper(
    {
      sessionId,
      isCustomContext: forCustom,
      mappingContext,
      editingContextId: editingContext,
      // addBeforeSaveCallback?: AddSaveCallbackHandler;
      loading: {
        isLoading: !editingContext,
        height: 400,
      },
    },
    {
      ...defaultPropertyEditorContext,
      onChange: onEditorChange,
      refreshContext: () => {
        // manually refresh only if the subscription handler is missing:
        // if (subScribeToUpdateEvents) {
        //   return;
        // }
      },
      refresh: () => {
        refresh();
      },
    },
    stepSearch
  );

  const awaitAbleContext: IAwaitableContext = {
    addPromise: props.addBeforeSavePromise ?? addEditPromise,
    removePromise: props.removeBeforeSavePromise ?? removeEditPromise,
  };

  const handleAnyDragDrop = (_e: React.MouseEvent) => {
    // should this be vanilla on the document root?
    // we have all the state we need to handle the drop decision in the store itself:
    dropConnector(snapshot);
  };

  const flowColumnClass = forInput ? 'dp-mapping-editor__left-column' : 'dp-mapping-editor__right-column';
  const stepColumnClass = forInput ? 'dp-mapping-editor__right-column' : 'dp-mapping-editor__left-column';

  const { allStepNodes, visibleStepNodes } = useMemo(() => {
    if (!snapshot) {
      return { allStepNodes: [], visibleStepNodes: [] };
    }
    // copy filtered into copy of snapshot, since many existing utils work on the root node
    const { allStepNodes, visibleStepNodes, mappedDataPaths } = getStepNodeData(
      allEditors,
      filteredEditors,
      expandedStepNodes
    );
    //Set the mapped flow data paths so that filter/search happens prior to getMappings
    setMappedFlowDataPaths(mappedDataPaths);

    return { allStepNodes, visibleStepNodes };
    // because of how update events will update the node inside the mapping, but not affect the Children compared in
    // memoizing allEditors, we need to also update this just any time the snapshot changes, since that should be
    // different on each update event.
    // Although! this does make me question the decision to build this allStepNodes object to get the visibleStepNodes...
  }, [allEditors, expandedStepNodes, filteredEditors, snapshot, setMappedFlowDataPaths]);

  // can we also pull `validationIssuesForDesc` out of get mappings?
  const { mappings, validationIssuesForDesc } = useMemo(
    () =>
      getMappings(
        allStepNodes,
        visibleFlowDataNodes,
        filteredFlowDataNodes,
        visibleStepNodes,
        forInput,
        bothFiltersUnmapped
      ),
    [allStepNodes, forInput, visibleFlowDataNodes, visibleStepNodes, filteredFlowDataNodes, bothFiltersUnmapped]
  );

  useEffect(() => {
    // pass mappings to the store, even though it's "derived state", to avoid props drilling and
    // unnecessary re-renders of intermediary components.
    cacheMappings(mappings);
  }, [cacheMappings, mappings]);

  return (
    <PropertyEditorContextProvider
      value={context}
      tabs={{ tabIndex }}
      awaitable={awaitAbleContext}
      editorScrollContainer={null} // TODO scroll container up from the column component? which editor need this?
    >
      {/* provide store instance to child components */}
      <MappingEditorStore.Provider value={useStore}>
        <section id={id} className={getClassName()} onMouseUpCapture={handleAnyDragDrop}>
          <FlowMappingColumn className={flowColumnClass} validationIssues={validationIssuesForDesc} />
          <StepMappingColumn className={stepColumnClass} nodes={filteredEditors} />
          <MappingConnectors
            className="dp-mapping-editor__connectors"
            visibleStepNodes={visibleStepNodes}
            visibleFlowDataNodes={visibleFlowDataNodes}
          />
        </section>
      </MappingEditorStore.Provider>
    </PropertyEditorContextProvider>
  );
}

function getClassName() {
  return classNames('dp-mapping-editor');
}
